import { useState, useEffect } from "react";
import Bandera from "./components/Banderas";
import "../css/DragDrop.css";

import {
  DndContext,
  closestCenter
} from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";

interface Seleccion {
  id: number;
  nombre: string;
  bandera: string;
}

interface DragDropProps {
  grupo: string;
  equipos: Seleccion[];
  bloqueado: boolean;
  onGuardarOrden?: (orden: Seleccion[]) => void;
  estado: "inicial" | "esperando" | "parcial" | "perfecto" | "fallo";
}

/* =========================
   Item draggable
========================= */
const SortableItem = ({
  eq,
  index,
  grupo,
  bloqueado
}: {
  eq: Seleccion;
  index: number;
  grupo: string;
  bloqueado: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: eq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const esDirecto = index < 2;
  const esTercero = index === 2;
  const esEliminado = index === 3;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(!bloqueado ? { ...attributes, ...listeners } : {})}
      className={`dragdrop-item
        ${esDirecto ? "clasificado" : ""}
        ${esTercero ? "tercero" : ""}
        ${esEliminado ? "eliminado" : ""}
        ${bloqueado ? "item-bloqueado" : ""}
      `}
    >
      <span className="drag-handle">⋮⋮</span>

      <Bandera
        grupo={grupo}
        nombre={eq.bandera.replace(/\.(png|webp)$/, "")}
        alt={eq.nombre}
        size={40}
      />
      <div className="dragdrop-info">
        <strong>{index + 1}°</strong>
        <span className="nombre-equipo">{eq.nombre}</span>
      </div>

      

      <span className="dragdrop-status">
        {esDirecto && "✔"}
        {esTercero && "⭐"}
        {esEliminado && "✘"}
      </span>
    </div>
  );
};

/* =========================
   Componente principal
========================= */
const DragDrop = ({
  grupo,
  equipos,
  bloqueado,
  onGuardarOrden,
  estado
}: DragDropProps) => {

  const [orden, setOrden] = useState<Seleccion[]>(equipos);

  useEffect(() => {
    setOrden(equipos);
  }, [equipos]);

  /* =========================
     Drag end
  ========================= */
  const handleDragEnd = (event: any) => {
    if (bloqueado) return;

    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = orden.findIndex(e => e.id === active.id);
    const newIndex = orden.findIndex(e => e.id === over.id);

    const nuevaLista = [...orden];
    const [movido] = nuevaLista.splice(oldIndex, 1);
    nuevaLista.splice(newIndex, 0, movido);

    setOrden(nuevaLista);
    onGuardarOrden?.(nuevaLista);
  };

  return (
    <div className={`grupo-card estado-${estado}`}>

      {/* VIDEOS */}
      {estado === "esperando" && (
        <video
          className="grupo-video"
          src="/estados2/esperando.webm?v=2"
          autoPlay
          muted
          loop
          playsInline
        />
      )}

      {estado === "perfecto" && (
        <video
          className="grupo-video"
          src="/estados2/perfecto.webm"
          autoPlay
          muted
          loop
          playsInline
        />
      )}

      {estado !== "esperando" && estado !== "perfecto" && (
        <div className="grupo-bg" />
      )}

      <div className="grupo-overlay" />

      <div className="grupo-content">

        {/* HEADER */}
        <div className="grupo-header">
          <h3>Grupo {grupo}</h3>
          <span className="grupo-badge">
            {estado === "inicial" && "Ordená tu predicción"}
            {estado === "esperando" && "🕒 Confirmado"}
            {estado === "parcial" && "✔ +50 pts"}
            {estado === "perfecto" && "🏆 +200 pts"}
            {estado === "fallo" && "❌ 0 pts"}
          </span>
        </div>

        <div className="dragdrop-container">

          {!bloqueado && (
            <p className="dragdrop-explicacion">
              <strong>4°</strong> eliminado · <strong>+50 pts</strong> acierto parcial · <strong>+200 pts</strong> perfecto
            </p>
          )}

          {bloqueado && (
            <div className="dragdrop-overlay">
              🔒 Fase de grupos cerrada
            </div>
          )}

          {/* =========================
             DND CONTEXT
          ========================= */}
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orden.map(e => e.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className={`dragdrop-list ${bloqueado ? "bloqueado" : ""}`}>
                {orden.map((eq, index) => (
                  <SortableItem
                    key={eq.id}
                    eq={eq}
                    index={index}
                    grupo={grupo}
                    bloqueado={bloqueado}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

        </div>
      </div>
    </div>
  );
};

export default DragDrop;